import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'common/hooks/state';
import {
  updateLastActivity,
  lockSession,
  unlockSession,
  selectLockEnabled,
  selectLastActivity,
  selectLockTimeout,
  selectTabAwayTimeout,
  selectIsLocked,
} from 'common/store/lockSlice';
import { selectToken, refreshToken } from 'features/login/authSlice';

// redux-persist's default key ('root', from store.ts's persistConfig),
// prefixed the way redux-persist/lib/storage always prefixes it.
const PERSIST_STORAGE_KEY = 'persist:root';

const activityDebounceMs = 30000; // Update activity timestamp every 30 seconds max

/**
 * Hook to track user activity and lock the session after inactivity.
 * Only runs on web platform, handles:
 * - User interaction events (mouse, keyboard, touch)
 * - Tab visibility changes (tabAwayTimeout)
 * - Periodic idle timeout checks (lockTimeout)
 *
 * Lock semantics enforced here (see lockSlice's REHYDRATE matcher for the
 * boot-time half of the contract):
 * - `lastActivity` only advances while UNLOCKED. Interacting with the lock
 *   screen (mouse move, typing a password) is NOT session activity — letting
 *   it refresh the persisted timestamp allowed a locked session to be
 *   bypassed by simply opening a new tab / reloading.
 * - The main effect's identity must depend only on stable booleans. It
 *   previously depended on callbacks that captured `lastActivity`, so every
 *   `updateLastActivity()` dispatch recreated them and re-ran the effect,
 *   which dispatched `updateLastActivity()` again — a self-sustaining
 *   dispatch/render loop that intermittently crashed app boot with React's
 *   "Maximum update depth exceeded". Volatile values are therefore read
 *   through a ref, never through effect deps.
 */
export const useActivityTracker = () => {
  const dispatch = useAppDispatch();
  const lockEnabled = useAppSelector(selectLockEnabled);
  const lastActivity = useAppSelector(selectLastActivity);
  const lockTimeout = useAppSelector(selectLockTimeout);
  const tabAwayTimeout = useAppSelector(selectTabAwayTimeout);
  const isLocked = useAppSelector(selectIsLocked);
  const token = useAppSelector(selectToken);
  // Boolean, not the token object: setCredentials creates a new token object
  // on every refresh, which must not tear the tracker down and re-ignite it.
  const hasToken = !!token;

  // Latest volatile values, readable from stable listeners without being
  // effect dependencies (see loop note above). `token` (the object, not just
  // hasToken) is held here so the storage listener can compare bearer strings
  // for cross-tab adoption without re-subscribing the effect on every refresh.
  const volatileRef = useRef({
    lockEnabled, lastActivity, lockTimeout, tabAwayTimeout, isLocked, hasToken, token,
  });
  volatileRef.current = {
    lockEnabled, lastActivity, lockTimeout, tabAwayTimeout, isLocked, hasToken, token,
  };

  const lastActivityUpdateRef = useRef<number>(Date.now());
  const tabHiddenTimeRef = useRef<number | null>(null);

  useEffect(() => {
    // Don't track if lock is disabled or no user logged in
    if (!lockEnabled || !hasToken) return undefined;

    // Debounced activity update — ignored entirely while locked so the lock
    // screen can never be bypassed by "activity" performed on it.
    const handleActivity = () => {
      const { current } = volatileRef;
      if (current.isLocked) return;

      const now = Date.now();
      if (now - lastActivityUpdateRef.current >= activityDebounceMs) {
        lastActivityUpdateRef.current = now;
        dispatch(updateLastActivity());
      }
    };

    // Lock after lockTimeout of idle time (checked every 30 seconds)
    const checkIdleInactivity = () => {
      const { current } = volatileRef;
      if (!current.lockEnabled || current.isLocked || !current.hasToken) return;

      if (Date.now() - current.lastActivity >= current.lockTimeout) {
        dispatch(lockSession());
      }
    };

    // Handle visibility change (tab switch/minimize) - uses tabAwayTimeout
    const handleVisibilityChange = () => {
      const { current } = volatileRef;
      if (!current.lockEnabled || current.isLocked || !current.hasToken) return;

      if (document.visibilityState === 'hidden') {
        // Tab became hidden - record the time
        tabHiddenTimeRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        // Tab became visible - check if we were away too long
        const hiddenTime = tabHiddenTimeRef.current;
        if (hiddenTime && Date.now() - hiddenTime >= current.tabAwayTimeout) {
          dispatch(lockSession());
        }
        tabHiddenTimeRef.current = null;
      }
    };

    // Cross-tab lock/unlock sync. The `storage` event fires in every OTHER
    // same-origin tab when one tab writes to localStorage — never in the tab
    // that made the write — so this can't self-trigger. Locking/unlocking in
    // one tab persists through redux-persist; react to that write here so a
    // sibling tab reflects it immediately instead of waiting for its own
    // idle timer or a reload.
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== PERSIST_STORAGE_KEY || !event.newValue) return;
      try {
        const persistedRoot = JSON.parse(event.newValue);
        if (typeof persistedRoot?.lock !== 'string') return;
        const persistedLock = JSON.parse(persistedRoot.lock);
        if (typeof persistedLock?.isLocked !== 'boolean') return;

        // Loop-safe: only dispatch when the persisted value actually
        // differs from this tab's current state. Dispatching here triggers
        // this tab's own persist write, which fires a new `storage` event
        // in every OTHER tab (again, never this one) — those tabs will see
        // their own state already matches and no-op, so this converges in
        // one hop rather than ping-ponging.
        const { current } = volatileRef;
        if (persistedLock.isLocked && !current.isLocked) {
          dispatch(lockSession());
        } else if (!persistedLock.isLocked && current.isLocked) {
          dispatch(unlockSession());
        }
      } catch {
        // Malformed/partial value mid-flush — ignore; the next settled
        // write will produce a valid event.
      }
    };
    window.addEventListener('storage', handleStorage);

    // Activity events to listen for
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic idle inactivity check (every 30 seconds)
    const checkInterval = setInterval(checkIdleInactivity, 30000);

    // Stamp activity once when tracking starts UNLOCKED (e.g. right after
    // login, when the in-memory timestamp may predate the login screen).
    // Locked boots must keep their stale timestamp — stamping "now" here was
    // half of the reload-bypass bug. REHYDRATE already stamps unlocked boots.
    if (!volatileRef.current.isLocked) {
      lastActivityUpdateRef.current = Date.now();
      dispatch(updateLastActivity());
    }

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      clearInterval(checkInterval);
    };
  }, [lockEnabled, hasToken, dispatch]);

  // Cross-tab token adoption. Kept in its OWN effect (not the lock one) and
  // gated only on `hasToken`, because a user may disable the lock feature yet
  // still needs a sibling tab's rotated bearer adopted. validate-token ROTATES
  // the token, so after one tab validates, other tabs hold a stale in-memory
  // bearer while persist:root carries the live one — read it here and adopt.
  useEffect(() => {
    if (!hasToken) return undefined;

    const handleAuthStorage = (event: StorageEvent) => {
      if (event.key !== PERSIST_STORAGE_KEY || !event.newValue) return;
      try {
        const persistedRoot = JSON.parse(event.newValue);
        if (typeof persistedRoot?.auth !== 'string') return;
        const persistedAuth = JSON.parse(persistedRoot.auth);
        const persistedToken: string | undefined = persistedAuth?.token?.token;
        const currentToken = volatileRef.current.token?.token ?? null;

        // Adoption is ONE-DIRECTIONAL by design:
        // - Only adopt a non-null token that actually DIFFERS from ours. Equal
        //   tokens no-op, so the echo write this dispatch triggers (seen only
        //   by OTHER tabs) converges in one hop — no ping-pong.
        // - A null/absent persisted token means another tab logged out. We
        //   deliberately do NOT auto-logout here: rtkErrorHandler already tears
        //   the session down on a real 401, and mirroring a remote logout from
        //   a storage event would race that path and could sign out a tab whose
        //   own bearer is still valid.
        if (persistedToken && persistedToken !== currentToken) {
          dispatch(refreshToken({ token: { token: persistedToken } }));
        }
      } catch {
        // Malformed/partial value mid-flush — ignore; the next settled write
        // will produce a valid event.
      }
    };

    window.addEventListener('storage', handleAuthStorage);
    return () => window.removeEventListener('storage', handleAuthStorage);
  }, [hasToken, dispatch]);
};

export default useActivityTracker;
